var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
/**
 * @module cercaliagl/control/Isochrones
 */
import tokml from 'tokml';
import LonLat from '../LonLat';
import Control from './Control';
import MapControls from './MapControls';
import Notification, { Severity } from '../Notification';
import Marker from '../Marker';
import Feature from '../Feature';
import { downloadXmlToFile } from '../util';
import ServiceIsochrone from '../service/Isochrone';
import { i18n } from '../dictionary';
import VControlButton from '../view/VControlButton';
import VIsochrones, { EventType as IsochronesEventType } from '../view/VIsochrones';
var MAXISOLEVEL = 3600000; // 60min in miliseconds
/**
 * @classdesc
 * <b>Javascript ES5</b>
 *
 * ```js
 * const control = new cercaliagl.control.Isochrones();
 * ```
 * <br/>
 * @extends {Control}
 * @api
 */
var Isochrones = /** @class */ (function (_super) {
    __extends(Isochrones, _super);
    /**
     * @constructor
     */
    function Isochrones() {
        var _this = this;
        var controlButton = new VControlButton('isochrones', ['path1', 'path2', 'path3']);
        var element = controlButton.getElement();
        var isochronesDialog = new VIsochrones();
        _this = _super.call(this, {
            element: element,
            className: 'cercaliagl.control.Isochrones',
            name: MapControls.ISOCHRONES,
            dialog: isochronesDialog.getElement(),
        }) || this;
        /**
         * @private
         * @type {VControlButton}
         */
        _this.controlButton_ = controlButton;
        /**
         * @private
         * @type {VIsochrones}
         */
        _this.isochronesDialog_ = isochronesDialog;
        /**
         * @private
         * @type {ServiceIsochrone}
         */
        _this.isochroneService_ = new ServiceIsochrone();
        /**
         * @private
         * @type {boolean}
         */
        _this.enabled_ = false;
        /**
         * @private
         * @type {Array.<Feature>}
         */
        _this.features_ = [];
        /**
         * @private
         * @type {Array.<Marker>}
         */
        _this.markers_ = [];
        /**
         * @type {import("../view/VIsochrones").IsochronesOption}
         */
        _this.isochronesOptions_ = {};
        /**
         * @private
         * @type {boolean}
         */
        _this.activeInteraction_ = false;
        /**
         * @private
         * @type {Function}
         */
        _this.handleClickMapBound_ = _this.handleClickMap_.bind(_this);
        _this.initialize_();
        return _this;
    }
    /**
     */
    Isochrones.prototype.initialize_ = function () {
        var _this = this;
        this.controlButton_.setTooltip(i18n('tooltipIsochrones'));
        this.element_.addEventListener('click', this.handleClick_.bind(this));
        // Events dialog.
        this.isochronesDialog_.on(IsochronesEventType.SUBMIT, function (isochronesOptions) { return _this.handleSubmit_(isochronesOptions); });
        this.isochronesDialog_.on(IsochronesEventType.REMOVE, function () { return _this.handleRemove_(); });
        this.isochronesDialog_.on(IsochronesEventType.SAVE_KML, function () { return _this.handleSaveKml_(); });
        this.isochronesDialog_.on(IsochronesEventType.CLOSE, function () { return _this.disable(); });
    };
    /**
     * @private
     */
    Isochrones.prototype.handleClick_ = function () {
        if (!this.enabled_) {
            this.enable();
        }
        else {
            this.disable();
            this.clearAll();
        }
    };
    /**
     * Enable isochrones control.
     * @override
     * @api
     */
    Isochrones.prototype.enable = function () {
        this.enabled_ = true;
        this.controlButton_.enable();
        this.isochronesDialog_.show();
    };
    /**
     * Disable isochrones control. Hide dialog and remove all features.
     * @override
     * @api
     */
    Isochrones.prototype.disable = function () {
        this.enabled_ = false;
        this.controlButton_.disable();
        this.isochronesDialog_.hide();
        this.clearAll();
        if (this.activeInteraction_) {
            var mbMap = this.getMap();
            mbMap.off('click', this.handleClickMapBound_);
            mbMap.getCanvasContainer().style.cursor = '';
        }
    };
    /**
     * @private
     * @param {import("../view/VIsochrones").IsochronesOptions} isochronesOptions Isochrones options
     */
    Isochrones.prototype.handleSubmit_ = function (isochronesOptions) {
        var map = this.getMap();
        this.isochronesOptions_ = isochronesOptions;
        map.once('click', this.handleClickMapBound_);
        map.getCanvasContainer().style.cursor = 'crosshair';
        this.activeInteraction_ = true;
    };
    /**
     * @private
     * @param {Event} e Event
     */
    Isochrones.prototype.handleClickMap_ = function (e) {
        var position = LonLat.fromMapboxgl(e.lngLat);
        this.doIsochroneCall_(position, this.isochronesOptions_);
    };
    /**
     * @private
     * @param {string} message Message.
     * @param {string} severity Severity.
     */
    Isochrones.prototype.showNotification_ = function (message, severity) {
        var cercaliaMap = this.getCercaliaMap();
        var notification = new Notification(message, severity);
        notification.display(cercaliaMap);
    };
    /**
     * @private
     * @param {LonLat} position Position
     * @param {import("../view/VIsochrones").IsochronesOptions} isochronesOptions Isochrones options
     */
    Isochrones.prototype.doIsochroneCall_ = function (position, isochronesOptions) {
        var _this = this;
        this.isochroneService_.clear();
        var isolevels = this.getIsolevels(isochronesOptions.isolevels, isochronesOptions.increment);
        // const isolevels = isochronesOptions.increment ? `${isochronesOptions.isolevels},${(isochronesOptions.isolevels * 2)}` : isochronesOptions.isolevels;
        this.isochroneService_.setSearch({
            position: position,
            weight: isochronesOptions.weight,
            isolevels: isolevels,
            method: isochronesOptions.method,
            inverse: isochronesOptions.inverse === 1,
            net: isochronesOptions.net,
        });
        this.isochronesDialog_.setLoading(true);
        this.isochroneService_.calculate(this.handleCalculateData_.bind(this, position), function () { return _this.isochronesDialog_.setLoading(false); });
    };
    /**
     * @private
     * @param {isolevels} isolevel number.
     * @param {increment} increment boolean.
     */
    // eslint-disable-next-line class-methods-use-this
    Isochrones.prototype.getIsolevels = function (isolevel, increment) {
        if (!increment) {
            return isolevel;
        }
        var currentIsolevels = "".concat(isolevel);
        var index = 2;
        while (isolevel * index < MAXISOLEVEL) {
            currentIsolevels += ",".concat((isolevel * index));
            index += 1;
        }
        return currentIsolevels;
    };
    /**
     * @private
     * @param {LonLat} position Position.
     * @param {import('../servers').IsochronesResult} data Isochrone response.
     */
    Isochrones.prototype.handleCalculateData_ = function (position, data) {
        var _this = this;
        var _a, _b;
        var cercaliaMap = this.getCercaliaMap();
        if ((_b = (_a = data === null || data === void 0 ? void 0 : data.cercalia) === null || _a === void 0 ? void 0 : _a.isochrones) === null || _b === void 0 ? void 0 : _b.isochrone) {
            if (Array.isArray(data.cercalia.isochrones.isochrone)) {
                data.cercalia.isochrones.isochrone.forEach(function (isochrone) {
                    _this.drawIsochrone(isochrone);
                });
            }
            else {
                this.drawIsochrone(data.cercalia.isochrones.isochrone);
            }
            var marker = new Marker(/** @type {import('../Marker').MarkerOptions} */ ({ position: position }));
            this.markers_.push(marker);
            cercaliaMap.addMarker(marker);
            cercaliaMap.centerToFeatures(this.features_);
            this.isochronesDialog_.setLoading(false);
            this.isochronesDialog_.setDrawn(true);
        }
    };
    /**
     * @private
     * @param {{level: string, value: string}} isochrone
     */
    Isochrones.prototype.drawIsochrone = function (isochrone) {
        var cercaliaMap = this.getCercaliaMap();
        var wkt = isochrone.value;
        var feature = new Feature({ wkt: wkt });
        cercaliaMap.addFeature(feature);
        this.features_.push(feature);
    };
    /**
     * @private
     */
    Isochrones.prototype.handleSaveKml_ = function () {
        if (this.features_.length > 0) {
            var filename = "cercalia_isochrones".concat(Date.now(), ".kml");
            var features_1 = [];
            this.features_.forEach(function (cercaliaFeature) {
                features_1.push({
                    type: 'Feature',
                    properties: {},
                    geometry: cercaliaFeature.getGeometry(),
                });
            });
            var featureCollection = {
                type: 'FeatureCollection',
                features: features_1,
            };
            var kml = tokml(featureCollection);
            downloadXmlToFile(kml, filename);
        }
        else {
            this.showNotification_(i18n('notificationErrorSavingKML'), Severity.ERROR);
        }
    };
    /**
     * @private
     */
    Isochrones.prototype.handleRemove_ = function () {
        this.clearAll();
    };
    /**
     * Clear all isochrones.
     * @api
     */
    Isochrones.prototype.clearAll = function () {
        var cercaliaMap = this.getCercaliaMap();
        cercaliaMap.removeFeatures(this.features_);
        cercaliaMap.removeMarkers(this.markers_);
        this.features_ = [];
        this.markers_ = [];
        this.isochronesDialog_.setDrawn(false);
    };
    /**
     * Close dialog
     * @api
     */
    Isochrones.prototype.closePopup = function () {
        this.isochronesDialog_.hide();
    };
    /**
     * @api
     * @return {boolean} Enabled
     */
    Isochrones.prototype.isEnabled = function () {
        return this.enabled_;
    };
    return Isochrones;
}(Control));
export default Isochrones;
//# sourceMappingURL=Isochrones.js.map